import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { formatCurrency } from "common/utils/currency"
import { isNaN } from "lodash"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"

const CardTotalesDocumento = ({
  values,
  setFieldValue,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: $TSMigrationFixMe
  setFieldValue: (
    field: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: $TSMigrationFixMe,
    shouldValidate?: boolean
  ) => Promise<void>
}) => {
  const { t } = useTranslation()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const calcularSumatorioSubtotal = (
    conceptos: $TSMigrationFixMe[]
  ): number => {
    return conceptos.reduce(
      (sumatorio, concepto) =>
        sumatorio + concepto.cantidad * concepto.precioVenta,
      0
    )
  }
  const calcularSumatorioDescuento = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    conceptos: $TSMigrationFixMe[],
    descuento: null | number
  ): number => {
    return calcularSumatorioSubtotal(conceptos) * ((descuento ?? 0) / 100)
  }

  const calculateSumatorioRetencion = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    conceptos: $TSMigrationFixMe[],
    descuento: null | number,
    retencion: null | number
  ): number => {
    if (retencion == null || isNaN(retencion)) {
      return 0 // Retorna 0 si la retención no es válida
    }
    return (
      (calcularSumatorioSubtotal(conceptos) -
        calcularSumatorioDescuento(conceptos, descuento)) *
      (retencion / 100)
    )
  }

  const agruparYSumarConceptos = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    conceptos: $TSMigrationFixMe[],
    descuento: null | number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): $TSMigrationFixMe[] => {
    const grupos = new Map<
      string,
      { porcentaje: number; tipoId: string; sumatorio: number; nombre: string }
    >()

    conceptos.forEach((concepto) => {
      const key = `${concepto.impuestoArticuloPorcentaje}-${concepto.impuestoArticuloTipoId}`

      if (!grupos.has(key)) {
        grupos.set(key, {
          porcentaje: concepto.impuestoArticuloPorcentaje,
          tipoId: concepto.impuestoArticuloTipoId,
          sumatorio: 0,
          nombre: concepto.impuestoNombre,
        })
      }

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const grupo = grupos.get(key)!
      grupo.sumatorio +=
        concepto.cantidad *
        concepto.precioVenta *
        (1 - (descuento ?? 0) / 100) *
        (concepto.impuestoArticuloPorcentaje / 100)
    })

    return Array.from(grupos.values()).map((grupo) => ({
      impuestoArticuloPorcentaje: grupo.porcentaje,
      impuestoArticuloTipoId: grupo.tipoId,
      sumatorio: grupo.sumatorio,
      nombre: grupo.nombre,
    }))
  }

  const calcularSumatorioTotales = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    conceptos: $TSMigrationFixMe[],
    descuento: null | number
  ): number => {
    const totalSinRetencion = calcularSumatorioSubtotal(conceptos)
    const retencionTotal = calculateSumatorioRetencion(
      values?.conceptos,
      values?.descuento,
      values?.retencionPorcentaje
    )
    const totalImp = agruparYSumarConceptos(
      values?.conceptos,
      values?.descuento
    ).reduce((acumulado, objeto) => acumulado + objeto.sumatorio, 0)

    if (descuento === null || descuento === 0) {
      return totalSinRetencion - (retencionTotal ?? 0) + totalImp
    }

    return (
      totalSinRetencion -
      totalSinRetencion * (descuento / 100) -
      (retencionTotal ?? 0) +
      totalImp
    )
  }

  useEffect(() => {
    setFieldValue(
      "totales.subtotal",
      calcularSumatorioSubtotal(values?.conceptos)
    )
    const sumatorioDescuento = calcularSumatorioDescuento(
      values?.conceptos,
      values?.descuento
    )

    setFieldValue(
      "totales.descuentoValor",
      values?.descuento
        ? values?.total < 0
          ? -sumatorioDescuento
          : sumatorioDescuento
        : 0
    )
    const sumatorioRetencion = calculateSumatorioRetencion(
      values?.conceptos,
      values?.descuento,
      values?.retencionPorcentaje
    )
    setFieldValue(
      "totales.retencionTotal",
      values?.retencionPorcentaje
        ? values?.total < 0
          ? -sumatorioRetencion
          : sumatorioRetencion
        : 0
    )
    setFieldValue(
      "totales.impuestosAgrupados",
      agruparYSumarConceptos(values?.conceptos, values?.descuento)
    )
    setFieldValue(
      "totales.totalValor",
      calcularSumatorioTotales(values?.conceptos, values?.descuento)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values?.conceptos,
    values?.descuento,
    values?.retencion,
    values?.retencionPorcentaje,
  ])

  return (
    <div className='tw-w-full tw-flex tw-flex-row tw-justify-end'>
      <div className='tw-flex tw-flex-row tw-justify-between tw-w-fit'>
        <div className='tw-flex tw-flex-col tw-w-auto tw-items-end '>
          <div className='tw-mb-3'>
            {t("crear.facturaRec.taxableAmountLabel")}
          </div>
          {values?.descuento && (
            <div className='tw-mb-3'>
              {t("crear.facturaRec.discountLabel")}
              {values?.descuento}%:
            </div>
          )}
          {values?.totales?.impuestosAgrupados?.map(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ({ impuestoArticuloPorcentaje }: $TSMigrationFixMe) => (
              <div
                className='tw-mb-3'
                key={`impuesto${impuestoArticuloPorcentaje}`}
              >
                {t("impuestos.table.typeGroup.iva")} (
                {impuestoArticuloPorcentaje}%):
              </div>
            )
          )}
          {values?.retencionPorcentaje && (
            <div className='tw-mb-3'>
              <span>
                {t("impuestos.table.typeGroup.withholding")}{" "}
                {values?.retencionPorcentaje}%:
              </span>
            </div>
          )}
          <div className='tw-mb-3 tw-font-medium'>
            {t("crear.facturaRec.totalLabel")}
          </div>
        </div>

        <div className='tw-flex tw-flex-col tw-w-auto tw-ml-24 tw-text-end'>
          <div
            className={`tw-mb-3 ${
              values?.descuento || values?.retencionPorcentaje ? "tw-mr-5" : ""
            }`}
          >
            {formatCurrency(values?.totales?.subtotal, false, values?.divisa)}
          </div>

          {values?.descuento && (
            <div className='tw-mb-3 tw-flex tw-flex-row tw-items-center tw-justify-end'>
              {formatCurrency(
                values?.totales?.descuentoValor,
                false,
                values?.divisa
              )}
              <FontAwesomeIcon
                onClick={() => setFieldValue("modals.showDescuentoModal", true)}
                icon={["fal", "pen"]}
                className='tw-ml-1 tw-cursor-pointer tw-w-4 tw-h-4 hover:tw-text-secondary'
              />
            </div>
          )}

          <div className=''>
            {values?.totales?.impuestosAgrupados?.map(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ({ sumatorio, index }: $TSMigrationFixMe) => (
                <div
                  className={`tw-mb-3 ${
                    values?.descuento || values?.retencionPorcentaje
                      ? "tw-mr-5"
                      : ""
                  }`}
                  key={`b${index}`}
                >
                  {formatCurrency(sumatorio, false, values?.divisa)}
                </div>
              )
            )}
          </div>

          {values?.retencionPorcentaje && (
            <div className='tw-mb-3 tw-flex tw-flex-row tw-items-center tw-justify-end'>
              {formatCurrency(
                values?.totales?.retencionTotal,
                false,
                values?.divisa
              ) || "0,00"}
              <FontAwesomeIcon
                className='tw-ml-1 tw-cursor-pointer tw-w-4 tw-h-4 hover:tw-text-secondary'
                onClick={() => setFieldValue("modals.showRetencionModal", true)}
                icon={["fal", "pen"]}
              />
            </div>
          )}
          <div
            className={`tw-font-medium tw-mb-3 ${
              values?.descuento || values?.retencionPorcentaje ? "tw-mr-5" : ""
            }`}
          >
            {formatCurrency(values?.totales?.totalValor, false, values?.divisa)}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CardTotalesDocumento
